import type { EduInvitePreviewDto } from '@dijitalapp/shared';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { api, errorMessageKey } from '../lib/api';
import { useApp } from '../lib/app-context';
import { useShell } from './Shell';

const TOKEN = /^[A-Za-z0-9_-]{32,64}$/;

/** `/edu-join#<token>`: a school invite (teacher, class or guardian), shown before it is used. */
export function JoinEduPage() {
  const { i18n } = useApp();
  const { t } = i18n;
  const { hash } = useLocation();
  const navigate = useNavigate();
  const { reload } = useShell();
  const token = hash.slice(1);
  const valid = TOKEN.test(token);
  const [preview, setPreview] = useState<EduInvitePreviewDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!valid) return;
    let alive = true;
    api<EduInvitePreviewDto>('POST', '/edu-invites/preview', { token }).then(
      (p) => alive && setPreview(p),
      (err: unknown) => alive && setError(t(errorMessageKey(err))),
    );
    return () => {
      alive = false;
    };
  }, [token, valid, t]);

  const accept = async () => {
    setBusy(true);
    setError(null);
    try {
      await api('POST', '/edu-invites/redeem', { token });
      await reload();
      navigate('/learn', { replace: true });
    } catch (err) {
      setError(t(errorMessageKey(err)));
    } finally {
      setBusy(false);
    }
  };

  const what =
    preview?.kind === 'teacher'
      ? t('learn.join.teacher', { school: preview.schoolName })
      : preview?.kind === 'guardian'
        ? t('learn.join.guardian', { school: preview.schoolName, name: preview.studentName ?? '' })
        : preview
          ? t('learn.join.class', { school: preview.schoolName, className: preview.className ?? '' })
          : '';

  return (
    <div className="content-inner">
      <div className="card stack" style={{ maxWidth: 480, margin: '0 auto' }}>
        <h1 style={{ fontSize: 'var(--text-lg)' }}>{preview?.schoolName ?? t('learn.join.title')}</h1>
        {!valid && <p role="alert">{t('join.missingToken')}</p>}
        {error && (
          <p role="alert" className="alert alert-error">
            {error}
          </p>
        )}
        {valid && !preview && !error && <p className="muted">{t('app.loading')}</p>}
        {preview && (
          <>
            <p>{what}</p>
            <p className="hint">{t(preview.kind === 'class' ? 'learn.join.classNote' : preview.kind === 'guardian' ? 'learn.join.guardianNote' : 'learn.join.teacherNote')}</p>
            <button className="btn btn-primary" type="button" disabled={busy} onClick={() => void accept()}>
              {t('learn.join.accept')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
